<style lang="scss" scoped>
.appointments {
  overflow: auto;
  height: calc(100vh - 150px);
  width: 100%;

  &--title {
    height: 50px;
    font-size: 18px;
    padding: 15px;
    font-weight: 500;
    background-color: #d6d9e4;
    margin: 0;
    position: sticky;
    position: -webkit-sticky;
    top: 0;
    vertical-align: middle;
    z-index: 1;
  }

  table {
    border-collapse: collapse;

    thead {
      position: sticky;
      position: -webkit-sticky;
      border-collapse: collapse;
      top: 50px;
      z-index: 1;

      th {
        box-sizing: content-box;
        background-color: #f0f1f4;
        color: #a6aab4;
        font-size: 14px;
        font-weight: 500;
        text-transform: uppercase;
        border: 1px #d6d9e4 solid;
      }

      th.nav-prev,
      th.nav-next {
        transition: all 0.2s;

        i {
          font-size: 25px;
        }

        &.nav-enabled {
          cursor: pointer;

          &:hover {
            background-color: var(--theme-color);
            color: white;
          }
        }
      }
    }

    tbody td {
      font-size: 11px;
      border: 1px #d6d9e4 solid;
      color: #a0a4b0;
      font-weight: 500;

      &.nav-cell {
        width: 50px;
      }

      &.inactive {
        cursor: not-allowed;
        background-color: #a0a4b0;
        color: white;
      }
    }
  }
}

@media (max-width: 1024px) {
  .appointments {
    overflow-y: auto;
    overflow-x: scroll;
    height: auto;
  }
}
</style>

<template>
  <div v-if="appointments" class="text-center appointments mx-xl-3 mx-lg-0">
    <h2 class="appointments--title">{{ date.toLocaleDateString() }}</h2>
    <table class="table table-sm">
      <thead>
        <tr class="appointments--navigation">
          <th class="nav-prev nav-cell" :class="{ 'nav-enabled': can_prev_box }" @click="prevBox">
            <i v-show="can_prev_box" class="fas fa-angle-left"></i>
          </th>
          <th v-for="(col, index) in displayed_columns" v-bind:key="index">
            {{ col.name }}
          </th>
          <th class="nav-next nav-cell" :class="{ 'nav-enabled': can_next_box }" @click="nextBox">
            <i v-show="can_next_box" class="fas fa-angle-right"></i>
          </th>
        </tr>
      </thead>
      <tbody v-if="loading">
        <tr>
          <td :colspan="num_boxes + 2">
            <h1>CARGANDO</h1>
          </td>
        </tr>
      </tbody>
      <!-- Is Vacation Day -->
      <tbody v-else-if="is_vacation">
        <tr>
          <td :colspan="num_boxes + 2">
            <h1>FESTIVO / VACACIONES</h1>
          </td>
        </tr>
      </tbody>
      <!-- Doctors Agenda -->
      <tbody v-else-if="mode == 'doctors'">
        <template v-for="(timeslot, index) in timeslots">
          <tr v-bind:key="index">
            <template v-if="timeslot.inactive">
              <td class="inactive nav-cell">{{ timeslot.name }}</td>
              <td class="inactive" v-for="col in displayed_columns" v-bind:key="col.id"></td>
              <td class="inactive nav-cell">{{ timeslot.name }}</td>
            </template>
            <template v-else>
              <td class="nav-cell">{{ timeslot.name }}</td>
              <template v-for="obj in getAppsForTimeSlot(timeslot, displayed_columns, index)">
                <agenda-time-slot @customerEnters="customerEnters" @cellClick="cellClicked" :key="obj.box.id"
                  :appointment="obj.appointment" :timeslot="timeslot" :tsindex="index" :num_boxes="num_boxes"
                  :box="obj.box" :doctors="doctors"
                  :available="isTimeSlotAvailable(timeslot, obj.box)"></agenda-time-slot>
              </template>
              <td class="nav-cell">{{ timeslot.name }}</td>
            </template>
          </tr>
        </template>
      </tbody>
      <tbody v-else>
        <template v-for="(timeslot, index) in timeslots">
          <tr v-bind:key="index">
            <template v-if="timeslot.inactive">
              <td class="inactive nav-cell">{{ timeslot.name }}</td>
              <td class="inactive" v-for="col in displayed_columns" v-bind:key="col.id"></td>
              <td class="inactive nav-cell">{{ timeslot.name }}</td>
            </template>
            <template v-else>
              <td class="nav-cell">{{ timeslot.name }}</td>
              <template v-for="obj in getAppsForTimeSlot(timeslot, displayed_columns, index)">
                <agenda-time-slot @customerEnters="customerEnters" @cellClick="cellClicked" :key="obj.box.id"
                  :appointment="obj.appointment" :timeslot="timeslot" :tsindex="index" :num_boxes="default_num_boxes"
                  :box="obj.box" :doctors="doctors" :available="true"></agenda-time-slot>
              </template>
              <td class="nav-cell">{{ timeslot.name }}</td>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  props: {
    date: { required: true },
    appointments: { required: true },
    availability: { required: true },
    clinic: { required: true },
    default_num_boxes: { default: 3 },
    mode: { default: 'boxes' },
    loading: { default: false },
    selected_doctor: { default: null },
  },
  data() {
    return {
      selected_box: 0,
    };
  },
  computed: {
    num_boxes() {
      switch (this.mode) {
        case 'doctors':
          return this.displayed_columns.length;
        default:
          return this.default_num_boxes;
      }
    },
    avail_for_doctors() {
      let ret = {};

      this.availability.forEach((a) => {
        const doctor_id = a.doctor_id;
        ret[doctor_id] = ret[doctor_id] || [];
        ret[doctor_id].push(a);
      });

      return ret;
    },
    timeslot_mappings() {
      return this.updateTimeSlotMappings();
    },
    doctors() {
      let ret = {};
      if (this.clinic) {
        this.clinic.doctors.forEach((doctor) => {
          ret[doctor.doctor.id] = doctor.doctor;
          ret[doctor.doctor.id].name = doctor.name;
        });
      }
      return ret;
    },
    displayed_columns() {
      switch (this.mode) {
        case 'doctors':
          return this.getDailyDoctorColumns();
        default:
          return this.getDailyBoxColumns();
      }
    },
    can_prev_box() {
      return this.mode == 'boxes' && this.selected_box > 0;
    },
    can_next_box() {
      if (!this.mode != 'boxes') return false;
      let total_boxes = this.appointments ? this.appointments.length : 0;
      return this.selected_box + this.num_boxes < total_boxes;
    },
    dayindex() {
      let d = this.date.getDay();
      if (d == 0) return 6;
      return d - 1;
    },
    is_vacation() {
      if (!this.clinic) return true;

      let slots = this.clinic.schedule.daily_timeslots[this.dayindex];
      if (!slots.checked) return true;

      let found = this.clinic.schedule.vacations.find((vacation) => {
        return vacation == this.date.toISODate();
      });

      if (found) return true;

      return false;
    },
    timeslots() {
      let ret = [];
      if (this.clinic) {
        let slots = this.clinic.schedule.daily_timeslots[this.dayindex];
        let slot_minutes = Number(this.clinic.schedule.slot_minutes);
        if (slots.checked) {
          slots.morning.start.forEach((slot) => {
            let d = moment(slot, "HH:mm");
            let from = moment(this.date).set({
              hour: d.hours(),
              minute: d.minutes(),
              second: 0,
            });

            let to = ret.push({
              hour: d.hours(),
              minute: d.minutes(),
              name: slot,
              from: from.toDate(),
              to: from.add(slot_minutes - 1, "m").toDate(),
            });
          });

          ret.push({
            inactive: true,
            name: "Desc",
          });

          slots.afternoon.start.forEach((slot) => {
            let d = moment(slot, "HH:mm");
            let from = moment(this.date).set({
              hour: d.hours(),
              minute: d.minutes(),
              second: 0,
            });

            let to = ret.push({
              hour: d.hours(),
              minute: d.minutes(),
              name: slot,
              from: from.toDate(),
              to: from.add(slot_minutes - 1, "m").toDate(),
            });
          });
        }
      }
      return ret;
    },
  },
  watch: {
  },
  methods: {
    isTimeSlotAvailable(tslot, doctor_box) {
      let doc = doctor_box.doctor;
      if (this.mode != 'doctors' || !doc) return true;

      let default_available = doc.default_available;
      let avails = this.avail_for_doctors[Number(doc.id)];

      if (!avails || !avails.length) return default_available;

      const tslot_interval = moment.range(tslot.from, tslot.to);

      for (const i in avails) {
        let a = avails[i];
        const avail_interval = moment.range(a.date_from, a.date_to);

        if (tslot_interval.intersect(avail_interval)) {
          return Boolean(a.is_available);
        }
      }

      return Boolean(default_available);
    },
    getDefaultDoctorBucket(doctor_id) {
      if (!doctor_id) {
        return {}
      }

      let doc = this.doctors[doctor_id];

      return {
        id: doc.id,
        name: doc.name,
        appointments: []
      };
    },
    isDoctorAvailableToday(doctor) {
      if (this.is_vacation) return false;
      if (this.selected_doctor && (this.selected_doctor != doctor.id)) return false;

      let doctor_avail = this.avail_for_doctors[doctor.id];
      if (doctor_avail && doctor_avail.length > 0) return true;

      return Boolean(doctor.default_available);
    },
    getDailyDoctorBuckets() {

      let doctor_buckets = {
      };

      Object.values(this.doctors).forEach((doctor) => {
        if (this.isDoctorAvailableToday(doctor)) {
          doctor_buckets[doctor.id] = {
            id: doctor.id,
            doctor: doctor,
            name: doctor.name,
            appointments: [],
          }
        }
      });

      return doctor_buckets;
    },
    getDailyDoctorColumns() {
      let ret = [];

      if (this.appointments && this.appointments.length > 0) {
        let doctor_buckets = this.getDailyDoctorBuckets();
        this.appointments.forEach((box) => {
          box.appointments.forEach((app) => {
            let doctor_id = app.doctor_id || 0;
            if (doctor_id && doctor_buckets[doctor_id]) {
              doctor_buckets[doctor_id].appointments.push(app);
            }
          });
        });

        ret = Object.values(doctor_buckets);
      }

      return ret;
    },
    getDailyBoxColumns() {
      let ret = [];
      if (this.appointments && this.appointments.length > 0) {
        for (let i = 0; i < this.num_boxes; i++) {
          let box = this.appointments[this.selected_box + i];
          if (box) {
            ret.push(box);
          }
        }
      }
      return ret;
    },
    formatTimeSlot(timeslot) {
      return (
        timeslot.hour.toString().padStart(2, "0") +
        ":" +
        timeslot.minute.toString().padStart(2, "0")
      );
    },
    updateTimeSlotRanges() {
      this.timeslots.forEach((timeslot) => {
        if (!timeslot.inactive) {
          let from = moment(this.date).set({
            hour: timeslot.hour,
            minute: timeslot.minute,
            second: 0,
          });

          timeslot.from = from.toDate();
          timeslot.to = from.add(14, "m").toDate();
        }
      });
    },
    updateTimeSlotMappings() {
      if (!this.clinic) {
        return;
      }

      let mappings = {};

      // Organize by box
      let displayed_columns = this.mode == 'doctors' ? this.displayed_columns : this.appointments;

      const slot_minutes = Number(this.clinic.schedule.slot_minutes);

      displayed_columns.forEach((box) => {
        let box_mappings = {};

        // Assign appointments to timeslots
        box.appointments.forEach((appointment) => {

          appointment.num_slots = 0;

          const app_range = moment.range(appointment.date_from, appointment.date_to);
          const minutes = Array.from(app_range.by('minutes')).length - 1;

          appointment.total_minutes = minutes;
          appointment.slot_minutes = slot_minutes;
          appointment.total_slots = Math.ceil(appointment.total_minutes / slot_minutes);

          const app_from = moment(appointment.date_from).toDate();
          const app_to = moment(appointment.date_to).toDate();

          this.timeslots.forEach((timeslot) => {
            if (!timeslot.inactive) {
              if (timeslot.from >= app_from && timeslot.to <= app_to) {
                let cur_app = box_mappings[timeslot.name];
                if (cur_app && (appointment != cur_app)) {
                  if (appointment.date_from >= cur_app.date_from) {
                    box_mappings[timeslot.name] = appointment;
                  }
                } else {
                  box_mappings[timeslot.name] = appointment;
                }
              }
            }
          });
        });

        // Count assigned slots for every mapping, doing it here
        // accounts for possible overlaps.
        let last_app = null;

        for (const name in box_mappings) {
          let app = box_mappings[name];

          if (app && !app.stop_adding) {

            if (app.num_slots) {
              app.num_slots += 1;
            } else {
              app.num_slots = 1;
            }

            if (last_app && (last_app != app)) {
              last_app.stop_adding = true;
            }
          }

          last_app = app;
        }

        // Asign the final map to the box
        mappings[box.id] = box_mappings;
      });

      return mappings;
    },
    getAppForTimeSlot(timeslot, box) {
      if (!this.clinic) return null;

      let box_mappings = this.timeslot_mappings[box.id];
      if (!box_mappings) return null;
      return box_mappings[timeslot.name] || null;
    },
    isFirstApp(appointment, timeslot, index) {
      if (index == 0) {
        return true;
      }

      let d = moment(appointment.date_from);
      return (
        d.minutes() == timeslot.minute && d.hours() == timeslot.hour
      );
    },
    getAppsForTimeSlot(timeslot, boxes, index) {
      if (!this.clinic) return [];

      let ret = [];
      boxes.forEach((b) => {
        let first = false;
        let app = this.getAppForTimeSlot(timeslot, b);
        if (app) {
          first = this.isFirstApp(app, timeslot, index);
        }

        ret.push({
          appointment: app,
          box: b,
          first: first
        });
      });

      return ret;
    },
    getDoctorForTimeSlot(timeslot, box) {
      let app = this.getAppForTimeSlot(timeslot, box);
      if (!app) return null;

      return this.doctors[app.doctor_id] || null;
    },
    customerEnters(appointment, timeslot, box) {
      this.$emit("customerEnters", appointment, timeslot, box);
    },
    cellClicked(appointment, timeslot, box) {
      this.$emit("appSelected", appointment, timeslot, box);
    },
    prevBox() {
      if (this.can_prev_box) {
        this.selected_box -= 1;
      }
    },
    nextBox() {
      if (this.can_next_box) {
        this.selected_box += 1;
      }
    },
  },
  mounted() {
    this.updateTimeSlotMappings();
  },
};
</script>
